/**
 * MyContainersPage — owner-side list of containers I organize + sender-side
 * list of containers I joined as participant. Two tabs.
 *
 * Mirrors Flutter `containers/my_containers_page.dart`.
 *   GET /api/cargo/containers/my              — organizer side
 *   GET /api/cargo/containers/participants/my — participant side
 */
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowRight, Container, Plus, Loader, Globe, ArrowLeftRight, Hash, Calendar,
} from 'lucide-react';
import { useMyContainers, useMyParticipants } from '@/hooks/useContainers';
import { AppLayout } from '@/components/layout/AppLayout';
import { Button } from '@/components/common/Button';
import {
  containerStatusDisplay,
  participantStatusDisplay,
  containerFillFraction,
  containerPriceLabel,
} from '@/utils/containerStatus';
import type { Container as ContainerT, ContainerParticipant } from '@/api/containers';

type Tab = 'organized' | 'joined';

export default function MyContainersPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>('organized');
  const organized = useMyContainers();
  const joined = useMyParticipants();

  return (
    <AppLayout>
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-2 text-text-secondary mb-4 hover:text-primary"
      >
        <ArrowRight size={18} /> {t('back', 'رجوع')}
      </button>

      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-xl font-bold text-primary flex items-center gap-2">
            <Container size={20} /> {t('my_containers', 'حاوياتي')}
          </h1>
          <Link to="/containers/create">
            <Button variant="primary">
              <Plus size={18} className="ml-1" />
              {t('new_container', 'إنشاء حاوية')}
            </Button>
          </Link>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-border mb-4">
          <TabBtn active={tab === 'organized'} onClick={() => setTab('organized')}>
            {t('containers_organized_tab', 'كمنظِّم')}
            {organized.data && (
              <span className="ml-2 text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                {organized.data.length}
              </span>
            )}
          </TabBtn>
          <TabBtn active={tab === 'joined'} onClick={() => setTab('joined')}>
            {t('containers_joined_tab', 'كمشارِك')}
            {joined.data && (
              <span className="ml-2 text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                {joined.data.length}
              </span>
            )}
          </TabBtn>
        </div>

        {tab === 'organized' ? (
          <OrganizedList loading={organized.isLoading} error={organized.error} containers={organized.data} />
        ) : (
          <JoinedList loading={joined.isLoading} error={joined.error} participants={joined.data} />
        )}
      </div>
    </AppLayout>
  );
}

function TabBtn({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      className={`px-4 py-3 font-semibold text-sm border-b-2 transition ${
        active
          ? 'border-primary text-primary'
          : 'border-transparent text-text-secondary hover:text-text-primary'
      }`}
    >
      {children}
    </button>
  );
}

function OrganizedList({
  loading, error, containers,
}: {
  loading: boolean; error: unknown; containers?: ContainerT[];
}) {
  const { t } = useTranslation();
  if (loading) return <Spinner />;
  if (error) return <ErrorBox message={t('list_load_failed', 'تعذّر تحميل الحاويات. حاول مجدداً.')} />;
  if (!containers || containers.length === 0) {
    return (
      <Empty
        icon={<Container size={48} className="text-text-tertiary mb-3" />}
        title={t('no_containers_organized', 'لم تنظّم أيّ حاوية بعد')}
        cta={
          <Link to="/containers/create">
            <Button variant="primary">{t('new_container', 'إنشاء حاوية')}</Button>
          </Link>
        }
      />
    );
  }
  return (
    <div className="space-y-3">
      {containers.map((c) => <ContainerCard key={c.id} container={c} />)}
    </div>
  );
}

function ContainerCard({ container }: { container: ContainerT }) {
  const { t } = useTranslation();
  const sd = containerStatusDisplay(container.status);
  const fill = containerFillFraction(container);
  const priceLabel = containerPriceLabel(container);

  return (
    <Link
      to={`/containers/${container.id}`}
      className="block bg-surface border border-border rounded-2xl p-4 hover:border-primary/40 transition-all"
    >
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <Hash size={14} className="text-text-tertiary" />
            <span className="font-bold text-text-primary" dir="ltr">{container.public_code}</span>
            {container.is_tentative && (
              <span className="text-[10px] px-1.5 py-0.5 bg-warning/10 text-warning rounded font-bold">
                {t('tentative', 'مبدئية')}
              </span>
            )}
          </div>
          <div className="text-sm text-text-primary mb-1 truncate">{container.title}</div>
          <div className="flex items-center gap-1 text-xs text-text-secondary">
            <Globe size={11} />
            <span dir="ltr">{container.origin_country?.toUpperCase()}</span>
            {container.origin_port_code && <span dir="ltr"> ({container.origin_port_code})</span>}
            <ArrowLeftRight size={11} />
            <span dir="ltr">{container.destination_country?.toUpperCase()}</span>
            {container.destination_port_code && <span dir="ltr"> ({container.destination_port_code})</span>}
          </div>
        </div>
        <span className={`text-xs px-2 py-1 rounded-full font-bold ${sd.color}`}>{sd.label}</span>
      </div>

      {/* Capacity bar (if capacity is set) */}
      {(container.max_weight_kg != null || container.max_cbm != null) && (
        <div className="mt-3">
          <div className="flex justify-between text-xs text-text-secondary mb-1">
            <span>{t('container_filled', 'الممتلئ')}</span>
            <span>{Math.round(fill * 100)}%</span>
          </div>
          <div className="w-full h-1.5 bg-border/40 rounded-full overflow-hidden">
            <div className="h-full bg-primary transition-all" style={{ width: `${fill * 100}%` }} />
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-3 text-xs text-text-tertiary mt-3 pt-3 border-t border-border/50">
        {priceLabel && <span><strong className="text-primary">{priceLabel}</strong></span>}
        {container.estimated_departure_at && (
          <span className="flex items-center gap-1">
            <Calendar size={11} />
            {new Date(container.estimated_departure_at).toLocaleDateString('ar-SY')}
          </span>
        )}
      </div>
    </Link>
  );
}

function JoinedList({
  loading, error, participants,
}: {
  loading: boolean; error: unknown; participants?: ContainerParticipant[];
}) {
  const { t } = useTranslation();
  if (loading) return <Spinner />;
  if (error) return <ErrorBox message={t('list_load_failed', 'تعذّر تحميل المشاركات. حاول مجدداً.')} />;
  if (!participants || participants.length === 0) {
    return (
      <Empty
        icon={<Container size={48} className="text-text-tertiary mb-3" />}
        title={t('no_participations', 'لم تنضم لأيّ حاوية بعد')}
        cta={
          <Link to="/containers/browse">
            <Button variant="primary">{t('browse_containers', 'تصفّح الحاويات المفتوحة')}</Button>
          </Link>
        }
      />
    );
  }
  return (
    <div className="space-y-3">
      {participants.map((p) => <ParticipantCard key={p.id} p={p} />)}
    </div>
  );
}

function ParticipantCard({ p }: { p: ContainerParticipant }) {
  const sd = participantStatusDisplay(p.status);
  return (
    <Link
      to={`/containers/participant/${p.id}`}
      className="block bg-surface border border-border rounded-2xl p-4 hover:border-primary/40 transition-all"
    >
      <div className="flex items-start justify-between gap-3 mb-2">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Hash size={14} className="text-text-tertiary" />
            <span className="font-bold text-text-primary" dir="ltr">{p.public_code}</span>
          </div>
          {p.container_title && (
            <div className="text-sm text-text-secondary truncate">{p.container_title}</div>
          )}
          <div className="text-xs text-text-tertiary mt-1">
            {p.est_weight_kg && <span>{p.est_weight_kg} kg</span>}
            {p.est_cbm && <span> · {p.est_cbm} m³</span>}
            {p.declared_value_usd != null && (
              <span> · ${Number(p.declared_value_usd).toLocaleString()}</span>
            )}
          </div>
        </div>
        <span className={`text-xs px-2 py-1 rounded-full font-bold ${sd.color}`}>{sd.label}</span>
      </div>
      {p.price_total_usd != null && p.price_total_usd > 0 && (
        <div className="text-xs text-text-tertiary pt-2 border-t border-border/50">
          السعر النهائي: <strong>${Number(p.price_total_usd).toLocaleString()}</strong>
        </div>
      )}
    </Link>
  );
}

function Spinner() {
  return (
    <div className="flex justify-center py-12 text-text-tertiary">
      <Loader size={28} className="animate-spin" />
    </div>
  );
}

function ErrorBox({ message }: { message: string }) {
  return (
    <div className="bg-error/10 text-error rounded-xl p-4 text-sm text-center">{message}</div>
  );
}

function Empty({ icon, title, cta }: { icon: React.ReactNode; title: string; cta?: React.ReactNode }) {
  return (
    <div className="bg-surface border border-border rounded-2xl p-10 text-center">
      <div className="inline-block">{icon}</div>
      <p className="text-text-secondary mb-4">{title}</p>
      {cta}
    </div>
  );
}
